import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbFileText } from "solid-icons/tb";
import { createMemo, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { parseSessionDocumentPayload, type SessionDocumentPayload } from "./sessionDocumentSchema";

interface SessionDocumentRendererProps {
  block: StreamingBlock;
}

/**
 * The card for a document the agent wrote into a live session's workspace. It
 * reopens the document where the host can show one; on a host that cannot (the
 * web console has no session workspace) it stays an inert receipt.
 */
export const SessionDocumentRenderer = (props: SessionDocumentRendererProps) => {
  const rt = useChatRuntime();
  const { t } = rt;

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<SessionDocumentPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseSessionDocumentPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {t("session_document_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <div class="flex items-center gap-2 overflow-hidden rounded-lg border border-primary-border bg-panel px-3 py-2">
            <TbFileText class="size-4 shrink-0 text-brand" />
            <div class="min-w-0">
              <div class="truncate text-sm font-medium text-primary-text">{data().title}</div>
              <div class="text-xs text-muted-text">{t("session_document_card.subtitle")}</div>
            </div>
            <Show when={rt.actions.openSessionDocument}>
              {(open) => (
                <button
                  type="button"
                  class="ml-auto shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-brand hover:bg-inset"
                  onClick={() => open()({ artifactId: data().artifactId })}
                >
                  {t("session_document_card.open")}
                </button>
              )}
            </Show>
          </div>
        )}
      </Show>
    </Show>
  );
};
